import React, { useState, useEffect } from 'react'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Divider,
  IconButton,
  Badge,
  MenuItem,
  Select,
  useMediaQuery,
  Avatar,
  Tooltip,
  alpha,
  ListItemButton,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// Icons
import HomeIcon from '@mui/icons-material/Home'
import PersonIcon from '@mui/icons-material/Person'
import NotificationsIcon from '@mui/icons-material/Notifications'
import DocumentIcon from '@mui/icons-material/Description'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import InboxIcon from '@mui/icons-material/Inbox'
import SellIcon from '@mui/icons-material/Sell'
import ApartmentIcon from '@mui/icons-material/Apartment'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import LogoutIcon from '@mui/icons-material/Logout'
import LanguageIcon from '@mui/icons-material/Language'

import { supabase } from './components/supabaseClient'

const drawerWidth = 260
const collapsedDrawerWidth = 70

const Layout = ({ children, toggleDarkMode, mode }) => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()

  // Detect if it's a mobile screen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // States
  const [collapsed, setCollapsed] = useState(false) // existing logic for desktop collapse
  const [mobileOpen, setMobileOpen] = useState(false) // state for mobile drawer
  const [unreadCount, setUnreadCount] = useState(0)
  const [language, setLanguage] = useState(localStorage.getItem('language') || i18n.language)
  const [user, setUser] = useState(null)
  const [profileData, setProfileData] = useState(null)
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null)

  // Fetch user data
  useEffect(() => {
    async function fetchUser() {
      const { data } = await supabase.auth.getUser()
      if (data?.user) {
        setUser(data.user)

        // Fetch profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name, avatar_url')
          .eq('id', data.user.id)
          .single()

        setProfileData(profile || {})
      }
    }
    fetchUser()
  }, [])

  const handleToggleDrawer = () => {
    if (isMobile) {
      // Toggle the mobile drawer
      setMobileOpen((prev) => !prev)
    } else {
      // Toggle the desktop collapsed state
      setCollapsed((prev) => !prev)
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    window.location.href = '/auth'
  }

  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false)
    }
  }, [location, isMobile])

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return

      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false)

      if (error) {
        console.error('Error fetching unread messages:', error)
      } else {
        setUnreadCount(count || 0)
      }
    }

    fetchUnreadCount()

    // Set up a polling interval to check for new messages (every 60 seconds)
    const intervalId = setInterval(fetchUnreadCount, 60000)

    return () => clearInterval(intervalId)
  }, [user])

  // Handle language change
  const handleChangeLanguage = (newLang) => {
    i18n.changeLanguage(newLang)
    setLanguage(newLang)
    localStorage.setItem('language', newLang) // Persist language selection
    setLanguageMenuAnchor(null)
  }

  // Menu items configuration for cleaner code
  const menuItems = [
    {
      text: t('menu.overview'),
      icon: <HomeIcon />,
      path: '/dashboard',
      active: location.pathname === '/dashboard',
    },
    {
      text: t('menu.discoverListings'),
      icon: <ApartmentIcon />,
      path: '/discover',
      active: location.pathname.startsWith('/discover'),
    },
    {
      text: t('menu.manageListings'),
      icon: <SellIcon />,
      path: '/listings',
      active: location.pathname.startsWith('/listings'),
    },
    {
      text: t('menu.watchList'),
      icon: <NotificationsIcon />,
      path: '/notifications',
      active: location.pathname.startsWith('/notifications'),
    },
    {
      text: t('menu.documents'),
      icon: <DocumentIcon />,
      path: '/documents',
      active: location.pathname.startsWith('/documents'),
    },
    {
      text: t('menu.user'),
      icon: <PersonIcon />,
      path: '/user',
      active: location.pathname === '/user',
    },
    {
      text: t('menu.inbox'),
      icon: (
        <Badge badgeContent={unreadCount} color="secondary">
          <InboxIcon />
        </Badge>
      ),
      path: '/inbox',
      active: location.pathname.startsWith('/inbox'),
    },
  ]

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />

      {/* Responsive Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleToggleDrawer}
        sx={{
          width: isMobile ? drawerWidth : collapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          '& .MuiDrawer-paper': {
            width: isMobile ? drawerWidth : collapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowX: 'hidden',
            transition: 'width 0.3s ease',
            pb: 2,
          },
        }}
      >
        {/* Logo and Title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            px: collapsed ? 1 : 3,
            py: 3,
          }}
        >
          {!collapsed && (
            <Typography
              variant="h5"
              component={Link}
              to="/dashboard"
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                background: 'linear-gradient(to right, #2563eb, #10b981)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('appName')}
            </Typography>
          )}
          <IconButton
            onClick={handleToggleDrawer}
            color="primary"
            sx={{
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
        </Box>

        {/* User profile section */}
        {!collapsed && user && (
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              src={profileData?.avatar_url}
              alt={profileData?.first_name || user.email}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
              }}
            >
              {profileData?.first_name ? profileData.first_name[0] : user.email[0].toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {profileData?.first_name
                  ? `${profileData.first_name} ${profileData.last_name || ''}`
                  : user.email.split('@')[0]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Menu Items */}
        <List sx={{ px: 2, flex: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={item.active}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: collapsed ? 1.2 : 2,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  minHeight: 48,
                  backgroundColor: item.active
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'transparent',
                  color: item.active ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 40,
                    color: item.active ? 'primary.main' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Settings section at bottom */}
        <Divider sx={{ mb: 1 }} />
        <List sx={{ px: 2 }}>
          {/* Dark mode toggle */}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={toggleDarkMode}
              sx={{
                borderRadius: 2,
                py: 1.2,
                px: collapsed ? 1.2 : 2,
                justifyContent: collapsed ? 'center' : 'flex-start',
                minHeight: 48,
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40 }}>
                {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText primary={mode === 'light' ? 'Dark Mode' : 'Light Mode'} />
              )}
            </ListItemButton>
          </ListItem>

          {/* Language selector */}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              sx={{
                borderRadius: 2,
                py: 1.2,
                px: collapsed ? 1.2 : 2,
                justifyContent: collapsed ? 'center' : 'flex-start',
                minHeight: 48,
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40 }}>
                <LanguageIcon />
              </ListItemIcon>
              {!collapsed && (
                <Select
                  value={language}
                  onChange={(e) => handleChangeLanguage(e.target.value)}
                  variant="standard"
                  sx={{
                    width: '100%',
                    '& .MuiSelect-select': {
                      py: 0,
                      fontWeight: 400,
                    },
                    '&:before, &:after': {
                      display: 'none',
                    },
                  }}
                  disableUnderline
                >
                  <MenuItem value="en">🇺🇸 English</MenuItem>
                  <MenuItem value="nl">🇳🇱 Dutch</MenuItem>
                  <MenuItem value="fr">🇫🇷 French</MenuItem>
                  <MenuItem value="es">🇪🇸 Spanish</MenuItem>
                </Select>
              )}
            </ListItemButton>
          </ListItem>

          {/* Logout Button */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleSignOut}
              sx={{
                borderRadius: 2,
                py: 1.2,
                px: collapsed ? 1.2 : 2,
                justifyContent: collapsed ? 'center' : 'flex-start',
                minHeight: 48,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: 'inherit' }}>
                <LogoutIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t('menu.logout')} />}
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Mobile menu toggle button - visible when drawer is closed */}
      {isMobile && !mobileOpen && (
        <IconButton
          color="primary"
          aria-label="open drawer"
          onClick={handleToggleDrawer}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1200,
            backgroundColor: 'background.paper',
            boxShadow: 3,
            '&:hover': {
              backgroundColor: 'background.paper',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          transition: 'margin 0.3s, width 0.3s',
          p: 0,
          width: {
            xs: '100%',
            md: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)`,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default Layout
